import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ActionListItem, Anchor } from "client/Components";
import { getIconTemplateData } from "../Activity/Activity";
import { ActivityCard } from "../Activity/Card/ActivityCard";
import { ActivityContent } from "../Activity/Content/ActivityContent";
import { AvailableNotificationViewModel } from "client/Api/Notifications/notifications";
import styles from "../ActivityFeedSpaces.module.less";
import { generateTrackingId } from "client/Misc/utils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";


export type FrontActivityProps = {
	categoryName: string;
	activity: AvailableNotificationViewModel;
	isDismissed: boolean;
	canBeDeletedByUser: boolean;
	onDelete(): void;
	onMute(): void;
	onActivityChanged(): void;
	showMoreItemsContent: ReactElement;
	onShowMoreItems(): void;
};

const {
	Description,
	Footer
} = ActivityCard;

const {
	ActionList,
	TimeIndication,
	Button
} = Footer;

export const FrontActivity = (props: FrontActivityProps): ReactElement => {
	const {
		isDismissed,
		categoryName,
		activity,
		canBeDeletedByUser,
		onDelete,
		onMute,
		showMoreItemsContent,
		onShowMoreItems,
		onActivityChanged
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	const {
		id,
		availableFrom,
		color
	} = activity;

	const hasActions: boolean = canBeDeletedByUser;
	const deleteHandler = useCallback(
		(): void => {
			if (canBeDeletedByUser) {
				onDelete();
			}
		},
		[canBeDeletedByUser, onDelete]
	);

	const muteHandler = useCallback(
		(): void => {
			onMute();
		},
		[onMute]
	);

	const trackingId: string = generateTrackingId("front-activity", `|${id}`);

	return (
		<Anchor
			className={styles.activityItem}
			onClick={onShowMoreItems}
			trackingId={trackingId}
		>
			<ActivityCard
				hasFooter
				icon={getIconTemplateData(activity, false)}
				categoryName={categoryName}
				color={color}
				isDismissed={isDismissed}
			>
				<Description>
					<div>
						<ActivityContent
							activity={activity}
							onActivityChanged={onActivityChanged}
						/>
					</div>
					<TimeIndication timestamp={parseDtoDate(availableFrom)} timezone={userTimezone} />
				</Description>
				<Footer>
					<Button
						onClick={onShowMoreItems}
						bubbleClick={false}
					>
						{showMoreItemsContent}
					</Button>
					{hasActions && (
						<ActionList
							trackingId={generateTrackingId(trackingId, "__actions")}
							menuTitle={categoryName}
						>
							<ActionListItem
								icon="bell-slash"
								text={localize("_Mute")}
								onClick={muteHandler}
								trackingId="mute"
							/>
							{canBeDeletedByUser && (
								<ActionListItem
									icon="trash-can"
									text={localize("_DeleteActivities")}
									onClick={deleteHandler}
									trackingId="delete-stack"
								/>
							)}
						</ActionList>
					)}
				</Footer>
			</ActivityCard>
		</Anchor>
	);
};

FrontActivity.displayName = "FrontActivity";